import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import { Add, Edit, History } from "@mui/icons-material";
import {
	Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TableBody, TableCell,
	TableHead, TableRow, TextField, Tooltip, Typography
} from "@mui/material";
import Layout from "../../../Components/Layout/Layout";
import CompaniesNavigation from "Navigation/CompaniesNavigation";
import PaperTable from "../../../Components/PaperTable";
import Tile from "Components/Tiles/Tile";
import Sidebar from "Components/Sidebar/Sidebar";
import SidebarGroup from "Components/Sidebar/SidebarGroup";
import SidebarButton from "Components/Sidebar/SidebarButton";
import dateFormats from "system/dateFormats";
import { DieselSurcharge } from "../../../system/Domain";

const dateOnly = (value?: string | null) => (value ? moment(value).format("YYYY-MM-DD") : "");
const formatDate = (value?: string | null) => (value ? moment(value).format(dateFormats.date) : "offen");
const formatPercentage = (value: number) => `${value.toLocaleString("de-DE", { maximumFractionDigits: 2 })} %`;

const emptyRate = (): Partial<DieselSurcharge> => ({
	percentage: undefined,
	validFrom: moment().format("YYYY-MM-DD"),
	validTo: null,
	comment: ""
});

const DieselSurchargePage = () => {
	const [rates, setRates] = useState<DieselSurcharge[]>();
	const [editing, setEditing] = useState<Partial<DieselSurcharge>>();
	const [saving, setSaving] = useState(false);
	const [history_, setHistory] = useState<{ rate: DieselSurcharge; revisions: DieselSurcharge[] }>();
	const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
	const [rateAtDate, setRateAtDate] = useState<DieselSurcharge | null>();

	const load = useCallback(async () => {
		const { data } = await axios.get<DieselSurcharge[]>("/api/diesel-surcharges");
		setRates(data);
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	useEffect(() => {
		if (!date) {
			setRateAtDate(undefined);
			return;
		}

		let cancelled = false;
		axios.get<DieselSurcharge | "">("/api/diesel-surcharges/at", { params: { date } }).then(({ data }) => {
			if (!cancelled) {
				setRateAtDate(data || null);
			}
		});

		return () => {
			cancelled = true;
		};
	}, [date, rates]);

	const save = async () => {
		if (!editing) {
			return;
		}

		setSaving(true);
		try {
			const body = {
				...editing,
				percentage: Number(editing.percentage),
				validTo: editing.validTo || null
			};

			if (editing.id) {
				await axios.put(`/api/diesel-surcharges/${editing.id}`, body);
			} else {
				await axios.post("/api/diesel-surcharges", body);
			}

			setEditing(undefined);
			await load();
		} finally {
			setSaving(false);
		}
	};

	const showHistory = async (rate: DieselSurcharge) => {
		const { data } = await axios.get<DieselSurcharge[]>(`/api/diesel-surcharges/${rate.id}/history`);
		setHistory({ rate, revisions: data });
	};

	const today = moment().startOf("day");
	const current = rates?.find(r => moment(r.validFrom).isSameOrBefore(today) && (!r.validTo || moment(r.validTo).isSameOrAfter(today)));

	return (
		<Layout
			title="Dieselzuschlag"
			search="disabled"
			navigation={<CompaniesNavigation />}
			sidebar={(
				<Sidebar>
					<SidebarGroup>
						<SidebarButton color="primary" startIcon={<Add />} label="Neuer Satz" onClick={() => setEditing(emptyRate())} />
					</SidebarGroup>
				</Sidebar>
			)}
		>
			<Box mb={4}>
				<Tile title="Aktueller Dieselzuschlag">
					<Typography variant="h4" component="div">
						{current ? formatPercentage(current.percentage) : "Kein Satz hinterlegt"}
					</Typography>
					{current && (
						<Typography variant="body2">
							gültig {formatDate(current.validFrom)} bis {formatDate(current.validTo)}, gilt für alle Frachtführer bei Tarifen mit Dieselzuschlag
						</Typography>
					)}
					<Box mt={2} display="flex" alignItems="center" gap={2}>
						<TextField
							variant="standard" type="date" label="Satz am Stichtag" InputLabelProps={{ shrink: true }}
							value={date} onChange={e => setDate(e.target.value)}
						/>
						<Typography>
							{rateAtDate === undefined ? "" : rateAtDate ? formatPercentage(rateAtDate.percentage) : "kein Satz hinterlegt"}
						</Typography>
					</Box>
				</Tile>
			</Box>

			{!rates && <CircularProgress size={48} />}
			{rates && (
				<PaperTable>
					<TableHead>
						<TableRow>
							<TableCell>Satz</TableCell>
							<TableCell>Gültig ab</TableCell>
							<TableCell>Gültig bis</TableCell>
							<TableCell>Kommentar</TableCell>
							<TableCell>Eingetragen von</TableCell>
							<TableCell>Zuletzt geändert</TableCell>
							<TableCell />
						</TableRow>
					</TableHead>
					<TableBody>
						{rates.map(r => (
							<TableRow key={r.id} hover selected={r.id === current?.id}>
								<TableCell>{formatPercentage(r.percentage)}</TableCell>
								<TableCell>{formatDate(r.validFrom)}</TableCell>
								<TableCell>{formatDate(r.validTo)}</TableCell>
								<TableCell>{r.comment}</TableCell>
								<TableCell>
									{r.createdBy?.name}
									<br />
									{moment(r.dateCreated).format(dateFormats.dateTime)}
								</TableCell>
								<TableCell>
									{r.dateUpdated ? `${r.updatedBy?.name ?? ""}, ${moment(r.dateUpdated).format(dateFormats.dateTime)}` : "–"}
								</TableCell>
								<TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
									<Tooltip title="Bearbeiten">
										<IconButton size="small" onClick={() => setEditing(r)}><Edit /></IconButton>
									</Tooltip>
									<Tooltip title="Änderungsverlauf">
										<IconButton size="small" onClick={() => showHistory(r)}><History /></IconButton>
									</Tooltip>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</PaperTable>
			)}
			{rates?.length === 0 && <Typography mt={2}>Es ist noch kein Dieselzuschlag hinterlegt.</Typography>}

			<Dialog open={!!editing} onClose={() => setEditing(undefined)} fullWidth maxWidth="xs">
				<DialogTitle>{editing?.id ? "Dieselzuschlag ändern" : "Neuer Dieselzuschlag"}</DialogTitle>
				<DialogContent>
					<Box display="flex" flexDirection="column" gap={2} mt={1}>
						<TextField
							variant="standard" type="number" label="Satz (%)" autoFocus
							inputProps={{ min: 0, max: 100, step: 0.01 }}
							value={editing?.percentage ?? ""}
							onChange={e => setEditing(x => ({ ...x, percentage: e.target.value === "" ? undefined : Number(e.target.value) }))}
						/>
						<TextField
							variant="standard" type="date" label="Gültig ab" InputLabelProps={{ shrink: true }}
							value={dateOnly(editing?.validFrom)}
							onChange={e => setEditing(x => ({ ...x, validFrom: e.target.value }))}
						/>
						<TextField
							variant="standard" type="date" label="Gültig bis" InputLabelProps={{ shrink: true }}
							helperText="Leer = unbefristet. Zeiträume dürfen sich nicht überschneiden, ein offener Satz muss vorher beendet werden."
							value={dateOnly(editing?.validTo)}
							onChange={e => setEditing(x => ({ ...x, validTo: e.target.value || null }))}
						/>
						<TextField
							variant="standard" label="Kommentar"
							value={editing?.comment ?? ""}
							onChange={e => setEditing(x => ({ ...x, comment: e.target.value }))}
						/>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setEditing(undefined)}>Abbrechen</Button>
					<Button
						variant="contained" onClick={save}
						disabled={saving || editing?.percentage === undefined || !editing?.validFrom}
					>
						Speichern
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog open={!!history_} onClose={() => setHistory(undefined)} fullWidth maxWidth="md">
				<DialogTitle>Änderungsverlauf</DialogTitle>
				<DialogContent>
					<PaperTable>
						<TableHead>
							<TableRow>
								<TableCell>Version</TableCell>
								<TableCell>Satz</TableCell>
								<TableCell>Gültig ab</TableCell>
								<TableCell>Gültig bis</TableCell>
								<TableCell>Kommentar</TableCell>
								<TableCell>Geändert von</TableCell>
								<TableCell>Wann</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{history_?.revisions.map(r => (
								<TableRow key={r.version}>
									<TableCell>{r.version}</TableCell>
									<TableCell>{formatPercentage(r.percentage)}</TableCell>
									<TableCell>{formatDate(r.validFrom)}</TableCell>
									<TableCell>{formatDate(r.validTo)}</TableCell>
									<TableCell>{r.comment}</TableCell>
									<TableCell>{(r.updatedBy ?? r.createdBy)?.name}</TableCell>
									<TableCell>{moment(r.dateUpdated ?? r.dateCreated).format(dateFormats.dateTime)}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</PaperTable>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setHistory(undefined)}>Schließen</Button>
				</DialogActions>
			</Dialog>
		</Layout>
	);
};

export default DieselSurchargePage;
